import { useCallback, useRef } from 'react';
import useSubmissionProgress, {
  SubmissionStatusRecord,
  SubmissionValidationResult,
  SubmitAcceptedResponse,
  SubmitErrorClassification,
} from '../../../util/customHooks/useSubmissionProgress';
import useSubmissionRecovery from '../../../util/customHooks/useSubmissionRecovery';
import {
  INVENTORY_REQUEST_DETAIL_MESSAGES,
  INVENTORY_RETURN_REVIEW_MESSAGES,
  INVENTORY_RETURN_REVIEW_UI,
  NOTIFY_TYPE,
} from '../../../util/constants';
import { isAcceptedAsyncSubmissionStatus } from '../../../util/submissionState';

export type ReviewSubmitResponse = SubmitAcceptedResponse & {
  message?: string | null;
  externalId?: string | null;
};

export type UseInventoryReturnReviewSubmissionOptions = {
  caseExternalId: string | null | undefined;
  openSnackBar: (type: string, message: string) => void;
  refetchReturn: () => void;
  navigateToReviewQueue: () => void;
};

export type UseInventoryReturnReviewSubmissionResult = {
  handleReviewResponse: (response?: ReviewSubmitResponse | null) => void;
  isSubmissionPending: boolean;
};

/**
 * Follows a queued Inventory Return review to its outcome, showing the review screen's existing
 * messages. The review keeps its own submit handler because the mutation answers two ways: a
 * queued review returns a submission handle, while the label-ready branch completes during the
 * request and returns none. Only a handle is followed; a reply without one keeps today's path.
 */
const useInventoryReturnReviewSubmission = ({
  caseExternalId,
  openSnackBar,
  refetchReturn,
  navigateToReviewQueue,
}: UseInventoryReturnReviewSubmissionOptions): UseInventoryReturnReviewSubmissionResult => {
  // Only a review started in this visit navigates on success. An outcome picked up on load still
  // gets its message, but the user stays where they are.
  const startedSubmissionIdRef = useRef<string | null>(null);

  useSubmissionRecovery(caseExternalId);

  // The review never calls attemptSubmit, so these three only satisfy the hook's contract.
  const validate = useCallback(
    async (): Promise<SubmissionValidationResult> => ({ valid: true }),
    []
  );
  const submit = useCallback(async (): Promise<SubmitAcceptedResponse> => ({}), []);
  const classifySubmitError = useCallback(
    (): SubmitErrorClassification => ({
      message: INVENTORY_REQUEST_DETAIL_MESSAGES.GENERIC_RETRY,
      canRetry: true,
    }),
    []
  );

  const showSubmitSuccessThenLeave = useCallback((): void => {
    openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_RETURN_REVIEW_MESSAGES.SUBMIT_SUCCESS);
    setTimeout(
      navigateToReviewQueue,
      INVENTORY_RETURN_REVIEW_UI.SUBMIT_SUCCESS_NAVIGATION_DELAY_MS
    );
  }, [navigateToReviewQueue, openSnackBar]);

  const showGenericError = useCallback((): void => {
    openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_DETAIL_MESSAGES.GENERIC_RETRY);
  }, [openSnackBar]);

  const handleTerminalSucceeded = useCallback(
    (statusRecord: SubmissionStatusRecord): void => {
      const startedInThisVisit =
        !!statusRecord.submissionId && statusRecord.submissionId === startedSubmissionIdRef.current;
      if (startedInThisVisit) {
        startedSubmissionIdRef.current = null;
        showSubmitSuccessThenLeave();
        return;
      }

      // The user stays on this Return, so it is reloaded to show where the review left it.
      refetchReturn();
      openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_RETURN_REVIEW_MESSAGES.SUBMIT_SUCCESS);
    },
    [openSnackBar, refetchReturn, showSubmitSuccessThenLeave]
  );

  const handleTerminalFailure = useCallback((): void => {
    refetchReturn();
    showGenericError();
  }, [refetchReturn, showGenericError]);

  const { isSubmissionPending, restoreInFlightSubmission } = useSubmissionProgress({
    caseExternalId,
    validate,
    submit,
    classifySubmitError,
    onTerminalSucceeded: handleTerminalSucceeded,
    onTerminalRejected: handleTerminalFailure,
    onTerminalError: handleTerminalFailure,
  });

  const handleReviewResponse = useCallback(
    (response?: ReviewSubmitResponse | null): void => {
      const submissionId = response?.submissionId;

      if (!submissionId) {
        // The label-ready branch completes during the request and returns no handle.
        if (response?.message === INVENTORY_RETURN_REVIEW_MESSAGES.RESPONSE_SUCCESS) {
          showSubmitSuccessThenLeave();
        }
        return;
      }

      // A handle that is not queued or processing, or one that cannot be followed without the
      // Return's key, is not a success this screen can confirm.
      if (!caseExternalId || !isAcceptedAsyncSubmissionStatus(response?.status)) {
        showGenericError();
        return;
      }

      startedSubmissionIdRef.current = submissionId;
      restoreInFlightSubmission(submissionId);
    },
    [caseExternalId, restoreInFlightSubmission, showGenericError, showSubmitSuccessThenLeave]
  );

  return { handleReviewResponse, isSubmissionPending };
};

export default useInventoryReturnReviewSubmission;
